// SA Planning and Design Code zones — all 65 across 9 parent categories,
// enumerated live from SAPPA's PropertyPlanningAtlasV18 MapServer on
// 2026-04-15 (layers 392–400). The `code` is SAPPA's abbreviation, `name`
// the full zone name, `minSitePerDwelling` a typical residential minimum in
// m² where subdivision for housing is meaningful — null where it isn't.
//
// IMPORTANT: these minimums are a FALLBACK. SAPPA layer 204 (Minimum
// Dwelling Allotment Size) publishes a per-parcel overlay that supersedes
// this table; `shared/scoring.js` prefers it when present.

export const ZONE_CATEGORIES = [
  'Capital City',
  'Activity',
  'Employment',
  'Infrastructure',
  'Natural Resources and Environment',
  'Neighbourhood and Corridor',
  'Rural',
  'Township and Settlement',
  'Tourism',
];

/** @type {Array<{ code: string, name: string, category: string, minSitePerDwelling: number|null }>} */
export const ZONES = [
  // ── Capital City ────────────────────────────────────────────────────
  { code: 'APL',     name: 'Adelaide Park Lands',   category: 'Capital City', minSitePerDwelling: null },
  { code: 'CC',      name: 'Capital City',          category: 'Capital City', minSitePerDwelling: 100 },
  { code: 'CL',      name: 'City Living',           category: 'Capital City', minSitePerDwelling: 120 },
  { code: 'CMS',     name: 'City Main Street',      category: 'Capital City', minSitePerDwelling: 100 },
  { code: 'CR',      name: 'City Riverbank',        category: 'Capital City', minSitePerDwelling: null },

  // ── Activity ───────────────────────────────────────────────────────
  { code: 'LAC',     name: 'Local Activity Centre',     category: 'Activity', minSitePerDwelling: 200 },
  { code: 'SAC',     name: 'Suburban Activity Centre',  category: 'Activity', minSitePerDwelling: 200 },
  { code: 'SMS',     name: 'Suburban Main Street',      category: 'Activity', minSitePerDwelling: 150 },
  { code: 'UAC',     name: 'Urban Activity Centre',     category: 'Activity', minSitePerDwelling: 150 },
  { code: 'UC(MS)',  name: 'Urban Corridor (Main Street)', category: 'Activity', minSitePerDwelling: 150 },

  // ── Employment ─────────────────────────────────────────────────────
  { code: 'E',       name: 'Employment',                      category: 'Employment', minSitePerDwelling: null },
  { code: 'E(BH)',   name: 'Employment (Bulk Handling)',      category: 'Employment', minSitePerDwelling: null },
  { code: 'E(En)',   name: 'Employment (Enterprise)',         category: 'Employment', minSitePerDwelling: null },
  { code: 'RE',      name: 'Resource Extraction',             category: 'Employment', minSitePerDwelling: null },
  { code: 'SE',      name: 'Strategic Employment',            category: 'Employment', minSitePerDwelling: null },
  { code: 'SInv',    name: 'Strategic Innovation',            category: 'Employment', minSitePerDwelling: null },
  { code: 'SB',      name: 'Suburban Business',               category: 'Employment', minSitePerDwelling: null },

  // ── Infrastructure ─────────────────────────────────────────────────
  { code: 'CwF',     name: 'Commonwealth Facilities',                 category: 'Infrastructure', minSitePerDwelling: null },
  { code: 'CF',      name: 'Community Facilities',                    category: 'Infrastructure', minSitePerDwelling: null },
  { code: 'Inf',     name: 'Infrastructure',                          category: 'Infrastructure', minSitePerDwelling: null },
  { code: 'Inf(A)',  name: 'Infrastructure (Airfield)',               category: 'Infrastructure', minSitePerDwelling: null },
  { code: 'Inf(FMF)',name: 'Infrastructure (Ferry and Marina Facilities)', category: 'Infrastructure', minSitePerDwelling: null },

  // ── Natural Resources and Environment ──────────────────────────────
  { code: 'CWOI',    name: 'Coastal Waters and Offshore Islands', category: 'Natural Resources and Environment', minSitePerDwelling: null },
  { code: 'Con',     name: 'Conservation',                        category: 'Natural Resources and Environment', minSitePerDwelling: null },
  { code: 'HF',      name: 'Hills Face',                          category: 'Natural Resources and Environment', minSitePerDwelling: null },
  { code: 'OS',      name: 'Open Space',                          category: 'Natural Resources and Environment', minSitePerDwelling: null },
  { code: 'Rec',     name: 'Recreation',                          category: 'Natural Resources and Environment', minSitePerDwelling: null },

  // ── Neighbourhood and Corridor ─────────────────────────────────────
  { code: 'BN',      name: 'Business Neighbourhood',               category: 'Neighbourhood and Corridor', minSitePerDwelling: 200 },
  { code: 'EN',      name: 'Established Neighbourhood',            category: 'Neighbourhood and Corridor', minSitePerDwelling: 400 },
  { code: 'GN',      name: 'General Neighbourhood',                category: 'Neighbourhood and Corridor', minSitePerDwelling: 300 },
  { code: 'GCE',     name: 'Golf Course Estate',                   category: 'Neighbourhood and Corridor', minSitePerDwelling: 500 },
  { code: 'HN',      name: 'Hills Neighbourhood',                  category: 'Neighbourhood and Corridor', minSitePerDwelling: 1200 },
  { code: 'HIn',     name: 'Home Industry',                        category: 'Neighbourhood and Corridor', minSitePerDwelling: 450 },
  { code: 'HDN',     name: 'Housing Diversity Neighbourhood',      category: 'Neighbourhood and Corridor', minSitePerDwelling: 200 },
  { code: 'MPN',     name: 'Master Planned Neighbourhood',         category: 'Neighbourhood and Corridor', minSitePerDwelling: 250 },
  { code: 'MPR',     name: 'Master Planned Renewal',               category: 'Neighbourhood and Corridor', minSitePerDwelling: 200 },
  { code: 'MPT',     name: 'Master Planned Township',              category: 'Neighbourhood and Corridor', minSitePerDwelling: 300 },
  { code: 'N',       name: 'Neighbourhood',                        category: 'Neighbourhood and Corridor', minSitePerDwelling: 300 },
  { code: 'RP',      name: 'Residential Park',                     category: 'Neighbourhood and Corridor', minSitePerDwelling: 150 },
  { code: 'RuN',     name: 'Rural Neighbourhood',                  category: 'Neighbourhood and Corridor', minSitePerDwelling: 1200 },
  { code: 'SN',      name: 'Suburban Neighbourhood',               category: 'Neighbourhood and Corridor', minSitePerDwelling: 300 },
  { code: 'TN',      name: 'Township Neighbourhood',               category: 'Neighbourhood and Corridor', minSitePerDwelling: 600 },
  { code: 'UC(Bo)',  name: 'Urban Corridor (Boulevard)',           category: 'Neighbourhood and Corridor', minSitePerDwelling: 150 },
  { code: 'UC(Bu)',  name: 'Urban Corridor (Business)',            category: 'Neighbourhood and Corridor', minSitePerDwelling: 200 },
  { code: 'UC(L)',   name: 'Urban Corridor (Living)',              category: 'Neighbourhood and Corridor', minSitePerDwelling: 150 },
  { code: 'UN',      name: 'Urban Neighbourhood',                  category: 'Neighbourhood and Corridor', minSitePerDwelling: 200 },
  { code: 'URN',     name: 'Urban Renewal Neighbourhood',          category: 'Neighbourhood and Corridor', minSitePerDwelling: 200 },
  { code: 'WN',      name: 'Waterfront Neighbourhood',             category: 'Neighbourhood and Corridor', minSitePerDwelling: 300 },

  // ── Rural ──────────────────────────────────────────────────────────
  { code: 'DU',      name: 'Deferred Urban',               category: 'Rural', minSitePerDwelling: null },
  { code: 'PRuL',    name: 'Productive Rural Landscape',   category: 'Rural', minSitePerDwelling: 40000 },
  { code: 'RA',      name: 'Remote Areas',                 category: 'Rural', minSitePerDwelling: null },
  { code: 'Ru',      name: 'Rural',                        category: 'Rural', minSitePerDwelling: 40000 },
  { code: 'RuAq',    name: 'Rural Aquaculture',            category: 'Rural', minSitePerDwelling: null },
  { code: 'RuH',     name: 'Rural Horticulture',           category: 'Rural', minSitePerDwelling: 40000 },
  { code: 'RuIE',    name: 'Rural Intensive Enterprise',   category: 'Rural', minSitePerDwelling: null },

  // ── Township and Settlement ────────────────────────────────────────
  { code: 'RuL',     name: 'Rural Living',             category: 'Township and Settlement', minSitePerDwelling: 2000 },
  { code: 'RuS',     name: 'Rural Settlement',         category: 'Township and Settlement', minSitePerDwelling: 1500 },
  { code: 'RuShS',   name: 'Rural Shack Settlement',   category: 'Township and Settlement', minSitePerDwelling: 600 },
  { code: 'T',       name: 'Township',                 category: 'Township and Settlement', minSitePerDwelling: 600 },
  { code: 'TAC',     name: 'Township Activity Centre', category: 'Township and Settlement', minSitePerDwelling: 400 },
  { code: 'TMS',     name: 'Township Main Street',     category: 'Township and Settlement', minSitePerDwelling: 400 },
  { code: 'WS',      name: 'Workers\u2019 Settlement',  category: 'Township and Settlement', minSitePerDwelling: 800 },

  // ── Tourism ────────────────────────────────────────────────────────
  { code: 'CTP',     name: 'Caravan and Tourist Park',  category: 'Tourism', minSitePerDwelling: null },
  { code: 'MP',      name: 'Motorsport Park',           category: 'Tourism', minSitePerDwelling: null },
  { code: 'TD',      name: 'Tourism Development',       category: 'Tourism', minSitePerDwelling: null },
];

// Indexes for fast lookup by code or name.
const BY_CODE = new Map(ZONES.map((z) => [z.code.toLowerCase(), z]));
const BY_NAME = new Map(ZONES.map((z) => [normaliseName(z.name), z]));

export function normaliseZone(zone) {
  if (!zone) return null;
  return normaliseName(zone);
}

function normaliseName(s) {
  return String(s)
    .toLowerCase()
    .replace(/\s+zone\b/, '')
    .replace(/[^\w\s()'-]/g, '')
    .trim();
}

/**
 * Look up a zone by either its SAPPA code (e.g. "GN") or its full name
 * ("General Neighbourhood"). Returns the full zone record or null.
 */
export function findZone(key) {
  if (!key) return null;
  const lc = String(key).toLowerCase().trim();
  if (BY_CODE.has(lc)) return BY_CODE.get(lc);
  const n = normaliseName(key);
  if (BY_NAME.has(n)) return BY_NAME.get(n);
  // Partial-contains match for things like "General Neighbourhood (TNV-1)"
  for (const [name, z] of BY_NAME) {
    if (n.includes(name) || name.includes(n)) return z;
  }
  return null;
}

export function minSitePerDwelling(zone) {
  const z = findZone(zone);
  return z?.minSitePerDwelling ?? null;
}

// Minimum frontage (m) by target dwelling count — generic fallback used
// when SAPPA layer 205 (per-parcel overlay) doesn't apply.
export const FRONTAGE_MIN_BY_DWELLINGS = Object.freeze({
  1: 0, 2: 15, 3: 18, 4: 22, 5: 25, 6: 28,
});

export function minFrontage(dwellings) {
  return FRONTAGE_MIN_BY_DWELLINGS[dwellings] ?? (dwellings * 5);
}
